// Partner Network platform console — leads/attribution/conversion, reward &
// commission approval, plans, analytics. Routes through the gateway's
// admin/partner-network segment → partner-network-service. Auth rides the
// global axios interceptor (authService).
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(/\/api(\/v1)?\/?$/, '/api/v1');
const BASE = `${API_V1}/admin/partner-network`;

export interface PNLead {
  id: string;
  referrer_type: string;
  partner_id?: string | null;
  company_name: string;
  country: string;
  industry?: string | null;
  contact_name: string;
  contact_email: string;
  status: string;
  attribution_status?: string | null;
  attribution_expires_at?: string | null;
  tenant_id?: string | null;
  created_at: string;
}
export interface PNReward {
  id: string; company_name: string; referrer_type: string; reward_type: string;
  reward_value?: number | null; currency: string; status: string;
  downstream_payable_type?: string | null; downstream_payable_id?: string | null;
}
export interface PNCommission {
  id: string; partner_id: string; tenant_id: string; eligible_revenue: number;
  amount: number; currency: string; rate_percentage?: number | null;
  paid_month_index?: number | null; status: string; calculated_at: string; paid_at?: string | null;
}

export const listLeads = (p: { attribution?: string; status?: string; channel?: string } = {}) =>
  axios.get<{ items: PNLead[]; total: number }>(`${BASE}/leads`, { params: p }).then((r) => r.data);
export const resolveAttribution = (id: string, decision: string, reason?: string) =>
  axios.post(`${BASE}/leads/${id}/resolve`, { decision, reason }).then((r) => r.data);
export const attributionAction = (id: string, body: { action: string; days?: number; to_partner_id?: string; reason: string }) =>
  axios.post(`${BASE}/leads/${id}/attribution`, body).then((r) => r.data);
export const setLeadStage = (id: string, stage: string) =>
  axios.post(`${BASE}/leads/${id}/stage`, { stage }).then((r) => r.data);
export const convertLead = (id: string, body: { tenant_id: string; complimentary_months?: number; complimentary_start?: string; commission_plan_id?: string }) =>
  axios.post(`${BASE}/leads/${id}/convert`, body).then((r) => r.data);

export const listPlans = () =>
  axios.get<{ commission_plans: any[]; reward_plans: any[] }>(`${BASE}/plans`).then((r) => r.data);
export const setPlanStatus = (kind: 'commission' | 'reward', id: string, status: string) =>
  axios.post(`${BASE}/plans/${kind}/${id}/status`, { status }).then((r) => r.data);
export const upsertCommissionPlan = (body: any) =>
  axios.post(`${BASE}/commission-plans`, body).then((r) => r.data);
export const upsertRewardPlan = (body: any) =>
  axios.post(`${BASE}/reward-plans`, body).then((r) => r.data);

export const listRewards = (status?: string) =>
  axios.get<{ items: PNReward[] }>(`${BASE}/rewards`, { params: { status } }).then((r) => r.data.items ?? []);
export const rewardAction = (id: string, action: string, reference?: string) =>
  axios.post(`${BASE}/rewards/${id}/${action}`, { reference }).then((r) => r.data);

export const listCommissions = (status?: string) =>
  axios.get<{ items: PNCommission[] }>(`${BASE}/commissions`, { params: { status } }).then((r) => r.data.items ?? []);
export const commissionAction = (id: string, action: string, reference?: string) =>
  axios.post(`${BASE}/commissions/${id}/${action}`, { reference }).then((r) => r.data);

export const getAnalytics = () => axios.get(`${BASE}/analytics`).then((r) => r.data);
